import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import styles from './ai-prompt-box-footer-right-actions.module.scss';
import type {AiPromptBoxFooterRightActionsProps} from './ai-prompt-box-footer-right-actions.types';
import {AiPromptBoxCreditsPill} from '@Components/ai-prompt-box/components/ai-prompt-box-credits-pill';
import {AiPromptBoxSubmitButton} from '@Components/ai-prompt-box/components/ai-prompt-box-submit-button';
import {AiRecordAudioButton} from '@Components/ai-prompt-box/components/ai-record-audio-button/ai-record-audio-button';
import {AiRecordingControls} from '@Components/ai-prompt-box/components/ai-recording-controls/ai-recording-controls';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import type {RootState} from '@/store';
import {useAudioLimit} from '@AiChat/hooks/useAudioLimit';
import {isUserLoggedIn} from '@Libraries/user.library';

export function AiPromptBoxFooterRightActions({id, onSubmit, onStop, onSoftStop, onRegenerate, allowSoftSendDuringUploads}: AiPromptBoxFooterRightActionsProps): ReactElement {
  const {state} = useAiPromptBox(id);
  const audioLimit = useSelector((s: RootState) => s.aiChat.audioLimit);
  const isFetchingAudioLimit = useSelector((s: RootState) => s.aiChat.isFetchingAudioLimit);
  const {fetchStatus} = useAudioLimit();

  const showMic = state.transcription?.showMic ?? false;
  const isRecordingActive = (state.isRecordingAudio ?? false) || (state.isProcessingAudio ?? false);

  useEffect(() => {
    if (!showMic || !isUserLoggedIn()) {
      return;
    }
    if (audioLimit.lastSyncedAt === null && !isFetchingAudioLimit) {
      void fetchStatus();
    }
  }, [showMic, audioLimit.lastSyncedAt, isFetchingAudioLimit, fetchStatus]);

  const renderMicButton = (): ReactElement | null => {
    if (!showMic) {
      return null;
    }

    return <AiRecordAudioButton id={id} />;
  };

  const renderAiRecordingControls = (): ReactElement | null => {
    if (!showMic) {
      return null;
    }

    return <AiRecordingControls id={id} />;
  };

  const renderActionButtons = (): ReactElement | null => {
    if (isRecordingActive) {
      return null;
    }

    return (
      <div className={styles.container}>
        <AiPromptBoxCreditsPill id={id} />
        {renderMicButton()}
        <AiPromptBoxSubmitButton
          id={id}
          onSubmit={onSubmit}
          onStop={onStop}
          onSoftStop={onSoftStop}
          onRegenerate={onRegenerate}
          allowSoftSendDuringUploads={allowSoftSendDuringUploads}
        />
      </div>
    );
  };

  return (
    <>
      {renderAiRecordingControls()}
      {renderActionButtons()}
    </>
  );
}
